import { useState, useRef, useMemo } from 'react';
import { scoreScale, fmtScore } from './trustShared';

// The draggable trust band (approved 2026-07-22, TODO.md — feel-test mock
// validated the physics; this is a faithful port of that mock's formulas).
// ObjectDetail's L0 band becomes traversable: drag across the lens spread and
// the marker + state pill + borrowing sentence follow. Design choices the
// mock validated, kept verbatim:
//   - the marker chases the BLEND, not the finger — gaussian lens-proximity
//     weighting means it never sits in empty space ("magnetic" physics);
//   - lens chips brighten to show whose view you're borrowing mid-drag;
//   - release snaps back to the viewer's real blend (inspecting, not
//     re-opining — the ViewpointDrawer stays the instrument of record).
// Idle, it renders like the static TrustBand (plus dots at each lens score);
// the drag-only panel appears underneath while a target is held.

const TAU = 0.18; // gaussian kernel width from the mock
const weightsFor = (scores, target) => {
  const w = scores.map((s) => Math.exp(-((s - target) ** 2) / (2 * TAU * TAU)));
  const sum = w.reduce((a, b) => a + b, 0);
  return w.map((x) => x / sum);
};
const blendWith = (scores, w) => scores.reduce((a, s, i) => a + s * w[i], 0);

// Drag-state thresholds from the mock (±0.15) — a live reading of the
// borrowed viewpoint, distinct from the page's three-state tension semantics.
const stateOf = (v) =>
  v >= 0.15
    ? { label: 'supported', color: scoreScale(0.7) }
    : v >= -0.15
    ? { label: 'contested', color: 'var(--wv-warn)' }
    : { label: 'disputed', color: scoreScale(-0.7) };

export default function TrustBandDrag({ scores, lenses, blend, width = 300, height = 22 }) {
  const wrapRef = useRef(null);
  const [target, setTarget] = useState(null); // null = the viewer's saved blend
  const [dragging, setDragging] = useState(false);

  // stable lens order from the registry metadata; every lens the band spans
  const entries = useMemo(
    () => lenses.map((l) => ({ id: l.id, label: l.label, score: scores[l.id] ?? 0 })),
    [lenses, scores]
  );
  const values = entries.map((e) => e.score);
  const min = Math.min(...values);
  const max = Math.max(...values);

  const x = (v) => ((Math.max(-1, Math.min(1, v)) + 1) / 2) * width;
  const active = target !== null;
  const w = active ? weightsFor(values, target) : null;
  const shown = active ? blendWith(values, w) : blend;
  const st = stateOf(shown);

  const vOf = (clientX) => {
    const rect = wrapRef.current.getBoundingClientRect();
    return Math.max(-1, Math.min(1, ((clientX - rect.left) / rect.width) * 2 - 1));
  };
  const release = () => {
    setDragging(false);
    setTarget(null); // snap back — the drawer stays the instrument of record
  };

  // whose view you're borrowing: top-2 weights above the mock's floor
  const top = active ? w.map((wi, i) => [wi, i]).sort((a, b) => b[0] - a[0]) : [];
  const names = active ? top.slice(0, 2).filter(([wi]) => wi > 0.12).map(([, i]) => entries[i].label) : [];
  const hotFloor = active ? (top[1] ? Math.min(top[1][0], 0.13) : 0.13) : 0;

  const bandX = x(min);
  const bandW = Math.max(x(max) - bandX, 2);

  return (
    <div>
      <div
        ref={wrapRef}
        role="slider"
        tabIndex={0}
        aria-label="Trust band — drag to borrow nearby lenses' view; release to snap back to your blend"
        aria-valuemin={-1}
        aria-valuemax={1}
        aria-valuenow={Number(shown.toFixed(2))}
        style={{ touchAction: 'none', cursor: dragging ? 'grabbing' : 'grab', outline: 'none', display: 'inline-block' }}
        onPointerDown={(e) => {
          setDragging(true);
          e.currentTarget.setPointerCapture(e.pointerId);
          setTarget(vOf(e.clientX));
        }}
        onPointerMove={(e) => { if (dragging) setTarget(vOf(e.clientX)); }}
        onPointerUp={release}
        onPointerCancel={release}
        onKeyDown={(e) => {
          if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
            e.preventDefault();
            const base = target === null ? blend : target;
            setTarget(Math.max(-1, Math.min(1, base + (e.key === 'ArrowRight' ? 0.05 : -0.05))));
          } else if (e.key === 'Home' || e.key === 'Escape') {
            e.preventDefault();
            setTarget(null);
          }
        }}
        onBlur={() => { if (!dragging) setTarget(null); }}
      >
        <svg width={width} height={height} style={{ display: 'block', overflow: 'visible' }}>
          <rect x={0} y={height / 2 - 3} width={width} height={6} rx={3} fill="var(--wv-surface2)" />
          <rect x={width / 2 - 0.75} y={1} width={1.5} height={height - 2} rx={0.75} fill="var(--wv-border-strong)" />
          {/* the lens spread — the meter's warn region with edge ticks (blend spec) */}
          <rect
            x={bandX}
            y={height / 2 - 5}
            width={bandW}
            height={10}
            rx={5}
            style={{ fill: 'color-mix(in srgb, var(--wv-warn) 22%, transparent)' }}
          />
          <rect x={bandX - 1} y={height / 2 - 8} width={2} height={16} rx={1} fill="var(--wv-warn)" opacity={0.75} />
          <rect x={bandX + bandW - 1} y={height / 2 - 8} width={2} height={16} rx={1} fill="var(--wv-warn)" opacity={0.75} />
          {entries.map((e) => (
            <circle key={e.id} cx={x(e.score)} cy={height / 2} r={3} fill={scoreScale(e.score)} opacity={0.9} />
          ))}
          {active && (
            <line
              x1={x(target)} x2={x(target)} y1={1} y2={height - 1}
              stroke="var(--wv-muted)" strokeWidth={1} strokeDasharray="3 3" opacity={0.6}
            />
          )}
          {/* the marker: a dot with a card-colored ring (the blend's signature) */}
          <circle cx={x(shown)} cy={height / 2} r={7} fill="var(--wv-text)" stroke="var(--wv-surface)" strokeWidth={3}>
            <title>{active ? `borrowed view: ${fmtScore(shown)}` : `your blend: ${fmtScore(shown)}`}</title>
          </circle>
        </svg>
        {/* the printed scale — the Instrument's discipline under the Field's ribbon */}
        <div
          className="wv-data"
          aria-hidden="true"
          style={{ display: 'flex', justifyContent: 'space-between', width, fontSize: '10px', color: 'var(--wv-quiet)', marginTop: '2px' }}
        >
          <span>−1.0</span><span>−0.5</span><span>0</span><span>+0.5</span><span>+1.0</span>
        </div>
      </div>

      {active && (
        <div style={{ marginTop: 6 }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 6px', marginBottom: 4 }}>
            {entries.map((e, i) => {
              const wi = w[i];
              const hot = wi >= hotFloor;
              return (
                <span
                  key={e.id}
                  className="lens-chip"
                  style={{
                    fontSize: 11,
                    opacity: 0.35 + 0.65 * Math.min(wi * 3.2, 1),
                    borderColor: hot ? 'var(--wv-accent)' : undefined,
                    color: hot ? 'var(--wv-text)' : undefined,
                    transition: 'opacity 0.12s, border-color 0.12s, color 0.12s',
                  }}
                >
                  {e.label} {fmtScore(e.score)}
                </span>
              );
            })}
          </div>
          <div style={{ fontSize: 13, color: 'var(--wv-muted)' }}>
            {names.length ? (
              <>
                Borrowing <strong style={{ color: 'var(--wv-text)' }}>{names.join(' and ')}</strong>: reads{' '}
                <strong style={{ color: st.color }}>{st.label}</strong> at {fmtScore(shown)}. Release to return to your blend.
              </>
            ) : (
              <>No lens lives here — the blend holds at {fmtScore(shown)}.</>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
